import { defineStore } from 'pinia';
import { useEventBus } from './eventBus';

export const useModalStore = defineStore('modal', {
	state: () => ({
		registeredModals: new Set(),
		
		activeModalId: null,
		
		modalHistory: [],
		
		settings: {
			classLayer: 'js-modal-layer',
			classLayerActive: 'modal-layer--active',
			
			wrapper: 'body',
			wrapperClass: 'modal-open',
			
			classModal: 'js-modal',
			classModalActive: 'modal-window--active'
		},
		
		eventBus: useEventBus(),
	}),

	getters: {
		isModalRegistered: (state) => (modalId) => state.registeredModals.has(modalId),
		isModalOpen: (state) => !!state.activeModalId,
		isModalActive: (state) => (modalId) => state.activeModalId === modalId,
		getAllModals: (state) => Array.from(state.registeredModals),
		config: (state) => (key) => state.settings[key] ?? null
	},

	actions: {
		registerModal(modalId) {
			if (!modalId) {
				console.warn('Modal Id is required');
				return;
			}

			this.registeredModals.add(modalId);
		},
		
		unregisterModal(modalId) {
			if (this.registeredModals.has(modalId)) {
				if (this.activeModalId === modalId) {
					this.close();
				}
				this.registeredModals.delete(modalId);
			}
		},
		
		openModal(modalId) {
			if (!this.registeredModals.has(modalId)) {
				console.warn(`Modal "${modalId}" is not registered`);
				return;
			}
			
			if (this.activeModalId && this.activeModalId !== modalId) {
				this.modalHistory.push(this.activeModalId);
			}
			
			this.activeModalId = modalId;
			this.onModalOpen(modalId);
			this.updateDOM(true);
		},
		
		closeModal() {
			if (!this.activeModalId) return;
			
			this.onModalClose(this.activeModalId);
			if (this.modalHistory.length > 0) {
				this.activeModalId = this.modalHistory.pop();
			} else {
				this.activeModalId = null;
				this.updateDOM(false);
			}
		},
		
		closeAll() {
			this.activeModalId = null;
			this.modalHistory = [];
			this.updateDOM(false);
		},
		
		updateDOM(isOpen) {
			
			if (!this.wrapperElement) {
				this.wrapperElement = document.querySelector(this.settings.wrapper);
			}

			if (isOpen) {
				this.wrapperElement?.classList.add(this.settings.wrapperClass);
				this.initGlobalHandlers();
			} else {
				this.wrapperElement?.classList.remove(this.settings.wrapperClass);
				this.removeGlobalHandlers();
			}
		},
		
		onModalOpen(modalId) {
			this.eventBus.emit('o2k:modal.open', { modalId: modalId });
		},

		onModalClose(modalId) {
			this.eventBus.emit('o2k:modal.close', { modalId: modalId });
		},
		
		initGlobalHandlers() {
			document.addEventListener('keydown', this.handleEsc.bind(this));
		},

		removeGlobalHandlers() {
			document.removeEventListener('keydown', this.handleEsc.bind(this));
		},

		handleEsc(e) {
			if (e.key === 'Escape' && this.activeModalId) {
				e.preventDefault();
				this.closeModal();
			}
		}
	}
});
